/**
 * DerivativeCell (Phase 3.3) — per-platform per-aspect cell of `<PostsGrid>`.
 *
 * Phase 3 brings:
 *   - Multi-item rendering: when the cell has more than one item (variants
 *     or series), the cell shows a header row + an inline list of items
 *     below; primary variant is hoisted with a ⭐ icon.
 *   - Variant primary promotion: each non-primary variant exposes a
 *     `[⭐ Make primary]` action that fires `contentPostGridService.promotePrimary`.
 *     The cell snapshot resolves on the next onSnapshot tick.
 *   - Stale badge per-item — derivatives whose `staleSince` is set after
 *     a master regen are visibly flagged.
 *   - Per-item delete via the trash icon.
 *
 * The cell still ships a single empty-state Generate affordance — the per-cell
 * dialog handles all four modes (single / variants / series / fresh-variants).
 */

import React, { useState } from 'react';
import {
  Box, Typography, Stack, IconButton, Tooltip, Chip, CircularProgress,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { contentPostGridService } from '../../services/ContentPostGridService';
import type { DerivativeCellModel } from '../../hooks/useTopicPostsGrid';
import type { PlatformId, Post } from '../../types';
import { CellTaskBadge } from './CellTaskBadge';

export interface DerivativeCellProps {
  projectId: string;
  topicId: string;
  aspectKey: string | null;
  platform: PlatformId;
  model: DerivativeCellModel | undefined;
  busy: boolean;
  /**
   * Phase 3 follow-up — backend task id for an in-flight generation that
   * has not yet produced a post doc. Owned by `PostsGrid`. When set AND
   * the cell is still empty, the empty state renders a live
   * `<CellTaskBadge>` instead of the wand.
   */
  pendingTaskId?: string | null;
  onGenerate: () => void;
  /** Phase 3.6 — click on a post item opens it in UnifiedPostDialog. */
  onOpenPost?: (post: Post) => void;
  /** Orphan-aspect rows: hide the generate wand on empty cells so users can't
   *  create more posts with a stale aspectKey. See MasterCell for full rationale. */
  disableGenerate?: boolean;
  disableGenerateReason?: string;
}

export const DerivativeCell: React.FC<DerivativeCellProps> = ({
  projectId, topicId, aspectKey, platform, model, busy, pendingTaskId,
  onGenerate, onOpenPost, disableGenerate, disableGenerateReason,
}) => {
  const testidBase = `posts-grid-cell-${aspectKey ?? 'noaspect'}-${platform}`;
  const state = model?.state ?? 'empty';
  const [opBusy, setOpBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (state === 'empty') {
    return (
      <Box
        sx={{
          p: 1, borderRadius: 1, border: '1px dashed',
          borderColor: 'divider',
          minHeight: 64, display: 'flex', alignItems: 'center',
          justifyContent: 'center',
        }}
        data-testid={testidBase}
      >
        {disableGenerate ? (
          <Tooltip title={disableGenerateReason ?? 'Orphan aspect — generation disabled. Edit or delete existing posts in this row.'}>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>—</span>
          </Tooltip>
        ) : pendingTaskId ? (
          // Live status while the dialog-triggered generation is in flight
          // and has not yet written a post-doc to Firestore. Auto-hides
          // when the task hits a terminal status.
          <CellTaskBadge
            taskId={pendingTaskId}
            data-testid={`${testidBase}-task-badge-pending`}
          />
        ) : (
          <Tooltip title={busy ? 'Generating…' : `Generate post — ${platform}`}>
            <span>
              <IconButton
                size="small"
                onClick={onGenerate}
                disabled={busy}
                data-testid={`${testidBase}-generate`}
                color="primary"
              >
                {busy ? <CircularProgress size={16} /> : <AutoFixHighIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
    );
  }

  const items = model!.items;

  const handlePromote = async (post: Post) => {
    setError(null);
    setOpBusy(post.id);
    try {
      await contentPostGridService.promotePrimary(projectId, {
        topicId,
        aspectKey,
        platform,
        newPrimaryId: post.id,
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOpBusy(null);
    }
  };

  const handleDelete = async (post: Post) => {
    if (!window.confirm('Delete this post?')) return;
    setError(null);
    setOpBusy(post.id);
    try {
      await contentPostGridService.deletePost(projectId, post.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOpBusy(null);
    }
  };

  return (
    <Box
      sx={{
        p: 1, borderRadius: 1, border: '1px solid',
        borderColor: state === 'stale' ? 'warning.main' : 'divider',
        minHeight: 64,
      }}
      data-testid={testidBase}
    >
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
        {model!.cellMode === 'variants' && items.length > 1 && (
          <Chip
            label={`${items.length} variants`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {model!.cellMode === 'series' && items.length > 0 && (
          <Chip
            label={`series · ${items.length}`}
            size="small"
            color="primary"
            variant="outlined"
          />
        )}
        {model!.cellMode === 'single' && (
          <Chip label={platform} size="small" variant="outlined" />
        )}
        {state === 'stale' && (
          <Chip label="stale" size="small" color="warning" />
        )}
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Add another via this cell">
          <IconButton
            size="small"
            onClick={onGenerate}
            disabled={busy}
            data-testid={`${testidBase}-generate`}
          >
            {busy ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 0.5 }}>
          {error}
        </Typography>
      )}

      {/* ── Items list ─────────────────────────────────────────── */}
      <Stack spacing={0.5}>
        {items.map((post) => {
          const isPrimary = post.isPrimaryVariant === true;
          const isStale = Boolean(post.staleSince);
          const isItemBusy = opBusy === post.id;
          return (
            <Stack
              key={post.id}
              direction="row"
              spacing={0.5}
              alignItems="flex-start"
              data-testid={`${testidBase}-item-${post.id}`}
              sx={{
                py: 0.5,
                borderTop: items[0] !== post ? '1px dashed' : 'none',
                borderColor: 'divider',
              }}
            >
              {model!.cellMode === 'variants' && items.length > 1 && (
                <Tooltip title={isPrimary ? 'Primary variant' : 'Make primary'}>
                  <span>
                    <IconButton
                      size="small"
                      disabled={isPrimary || isItemBusy}
                      onClick={() => handlePromote(post)}
                      data-testid={`${testidBase}-promote-${post.id}`}
                      sx={{ p: 0.25 }}
                    >
                      {isPrimary ? (
                        <StarIcon fontSize="small" color="warning" />
                      ) : (
                        <StarBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {model!.cellMode === 'series' && (
                <Chip
                  label={`#${(post.seriesIndex ?? 0) + 1}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10 }}
                />
              )}

              <Box
                sx={{
                  flex: 1, minWidth: 0,
                  cursor: onOpenPost ? 'pointer' : 'default',
                }}
                onClick={() => onOpenPost?.(post)}
                data-testid={`${testidBase}-open-${post.id}`}
              >
                <Typography
                  variant="body2"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    fontWeight: isPrimary ? 500 : 400,
                    '&:hover': onOpenPost
                      ? { textDecoration: 'underline', color: 'primary.main' }
                      : undefined,
                  }}
                >
                  {post.hook ?? post.title ?? '(post)'}
                </Typography>
                {isStale && (
                  <Typography variant="caption" color="warning.main">
                    stale — master changed
                  </Typography>
                )}
                <CellTaskBadge
                  taskId={(post as { lastTaskId?: string }).lastTaskId ?? null}
                  dense
                  data-testid={`${testidBase}-task-badge-${post.id}`}
                />
              </Box>

              <Tooltip title="Delete this post">
                <span>
                  <IconButton
                    size="small"
                    disabled={isItemBusy}
                    onClick={() => handleDelete(post)}
                    data-testid={`${testidBase}-delete-${post.id}`}
                    sx={{ p: 0.25 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};

export default DerivativeCell;
